import { Controller } from "@hotwired/stimulus";
import { clearSearchResultsSkeleton, skeletonizeSearchResults } from "../entrypoints/common";

export default class extends Controller {
  static targets = ['form', 'results', 'input'];

  connect() {
    if (!this.hasInputTarget) {
      console.warn('Input target is missing. Skipping input event listeners.');
      return;
    }

    this.restoreFocusAfterLoad = false;
    this.inFlightQuery = null;
    this.searchAbortController = null;
    this.keyboardNavActive = false;
    this.boundHandleFrameLoad = this.handleFrameLoad.bind(this);
    this.boundBeforeFetchRequest = this.handleBeforeFetchRequest.bind(this);
    this.boundHandleBlur = this.handleBlur.bind(this);
    this.boundHandleFocus = this.handleFocus.bind(this);
    this.boundHandleKeyDown = this.handleKeyDown.bind(this);
    this.boundHandleClickOutside = this.handleClickOutside.bind(this);
    this.boundHandleResultsMouseOver = this.handleResultsMouseOver.bind(this);
    this.boundHandleResultsMouseMove = this.handleResultsMouseMove.bind(this);

    this.inputTarget.addEventListener('blur', this.boundHandleBlur);
    this.inputTarget.addEventListener('focus', this.boundHandleFocus);
    this.inputTarget.addEventListener('keydown', this.boundHandleKeyDown);
    document.addEventListener('click', this.boundHandleClickOutside);
    document.addEventListener('turbo:frame-load', this.boundHandleFrameLoad);
    document.addEventListener('turbo:before-fetch-request', this.boundBeforeFetchRequest);
    this.element.addEventListener('mouseover', this.boundHandleResultsMouseOver);
    this.element.addEventListener('mousemove', this.boundHandleResultsMouseMove);

    if (this.isGlobalSearch) {
      this.applyShortcutHints();
    }
  }

  disconnect() {
    document.removeEventListener('turbo:frame-load', this.boundHandleFrameLoad);
    document.removeEventListener('turbo:before-fetch-request', this.boundBeforeFetchRequest);
    document.removeEventListener('click', this.boundHandleClickOutside);
    this.element.removeEventListener('mouseover', this.boundHandleResultsMouseOver);
    this.element.removeEventListener('mousemove', this.boundHandleResultsMouseMove);
    this.clearInlineSpotlight();
    this.searchAbortController?.abort();
  }

  get isGlobalSearch() {
    return this.hasFormTarget && this.formTarget.dataset.searchGlobal === 'true';
  }

  get isPaletteSearch() {
    return Boolean(this.element.closest('.global-search-palette__dialog'));
  }

  get isPageSearch() {
    return Boolean(this.globalSearchRoot?.classList.contains('global-search--page'));
  }

  isApplePlatform() {
    const platform = navigator.userAgentData?.platform || navigator.platform || '';
    return /Mac|iPhone|iPad|iPod/i.test(platform);
  }

  shortcutLabel() {
    return this.isApplePlatform() ? '⌘K' : 'Ctrl+K';
  }

  applyShortcutHints() {
    if (this.isPageSearch || document.body.classList.contains('turbo-native')) return;

    const label = this.shortcutLabel();
    const basePlaceholder = (this.inputTarget.getAttribute('placeholder') || 'Rechercher')
      .replace(/\s*[·•]\s*(⌘K|Ctrl\+K)\s*$/i, '')
      .replace(/\s*(⌘K|Ctrl\+K)\s*$/i, '')
      .trim();
    this.inputTarget.placeholder = `${basePlaceholder} · ${label}`;
  }

  get globalSearchRoot() {
    return this.element.querySelector('.global-search') ||
      (this.element.classList?.contains?.('global-search') ? this.element : null);
  }

  setInlineSpotlight(active) {
    if (!this.isGlobalSearch || this.isPaletteSearch || this.isPageSearch) return;

    const root = this.globalSearchRoot;
    if (!root) return;

    root.classList.toggle('global-search--active', active);
    document.body.classList.toggle('global-search-inline-active', active);

    if (active) {
      if (!this.boundSpotlightPointerDown) {
        this.boundSpotlightPointerDown = this.handleSpotlightPointerDown.bind(this);
      }
      document.addEventListener('pointerdown', this.boundSpotlightPointerDown, true);
    } else if (this.boundSpotlightPointerDown) {
      document.removeEventListener('pointerdown', this.boundSpotlightPointerDown, true);
    }
  }

  clearInlineSpotlight() {
    document.body.classList.remove('global-search-inline-active');
    document.querySelectorAll('.global-search--active').forEach((el) => {
      el.classList.remove('global-search--active');
    });
    if (this.boundSpotlightPointerDown) {
      document.removeEventListener('pointerdown', this.boundSpotlightPointerDown, true);
    }
  }

  handleSpotlightPointerDown(event) {
    if (!document.body.classList.contains('global-search-inline-active')) return;

    const root = this.globalSearchRoot;
    if (root?.contains(event.target)) return;

    // Sur les show, le controller enveloppe toute la page : on ne peut pas
    // s’appuyer sur this.element.contains — seulement sur .global-search.
    this.hideResults();
    this.setInlineSpotlight(false);
    if (this.hasInputTarget && document.activeElement === this.inputTarget) {
      this.inputTarget.blur();
    }
  }

  handleBeforeFetchRequest(event) {
    if (!this.isIndexSearchFetch(event)) return;

    this.searchAbortController?.abort();
    this.searchAbortController = new AbortController();
    event.detail.fetchOptions.signal = this.searchAbortController.signal;
  }

  isIndexSearchFetch(event) {
    const frameId = this.formTarget?.dataset.turboFrame;
    if (!frameId) return false;

    const headers = event.detail?.fetchOptions?.headers;
    const requestFrame = headers?.['Turbo-Frame'] || '';
    if (requestFrame !== frameId) return false;

    const fetchUrl = event.detail?.url;
    if (!fetchUrl) return true;

    const parsed = new URL(fetchUrl, window.location.origin);
    // Pagination/sort also target search_results — must not abort or coalesce those fetches.
    if (parsed.searchParams.has('page')) return false;
    if (parsed.searchParams.has('sort') || parsed.searchParams.has('sort_reverse')) return false;

    return true;
  }

  abortInFlightSearch() {
    this.searchAbortController?.abort();
    this.searchAbortController = null;
    this.inFlightQuery = null;
  }

  searchWithSource(e) {
    const input = e.currentTarget;
    clearTimeout(this.timeout);
    this.timeout = setTimeout(() => {
      if (this.hasResultsTarget) {
        this.resultsTarget.classList.remove('d-none');
        void this.resultsTarget.offsetWidth;
      }
      const hiddenInput = document.createElement("input");
      hiddenInput.type = "hidden";
      hiddenInput.name = "source";
      hiddenInput.value = "stimulus";
      this.formTarget.appendChild(hiddenInput);
      this.formTarget.requestSubmit();
      hiddenInput.remove();
    }, 500);
  }

  search() {
    if (this.inFlightQuery !== null && this.inputTarget.value !== this.inFlightQuery) {
      this.abortInFlightSearch();
    }

    this.keyboardNavActive = false;
    this.clearResultSelection();
    clearTimeout(this.timeout);
    this.timeout = setTimeout(() => {
      const query = this.inputTarget.value;
      const trimmedLength = query.trim().length;

      if (trimmedLength > 2) {
        if (this.hasResultsTarget) {
          this.resultsTarget.classList.remove('d-none');
          void this.resultsTarget.offsetWidth;
        }
        this.loadSearchResults();
      } else if (query.length === 0 || (this.isPageSearch && trimmedLength <= 2)) {
        if (this.hasResultsTarget) {
          this.resultsTarget.classList.remove('d-none');
          void this.resultsTarget.offsetWidth;
        }
        this.loadSearchResults();
      } else if (this.hasResultsTarget) {
        this.hideResults();
      }
    }, 500);
  }

  loadSearchResults() {
    if (!this.hasFormTarget) return;

    const form = this.formTarget;
    const frameId = form.dataset.turboFrame;
    if (!frameId) return;

    // Prefer the frame inside this index — never the show-page global #search_results.
    const frame =
      this.element.querySelector(`#${CSS.escape(frameId)}`) || document.getElementById(frameId);
    if (!frame) return;

    const url = this.buildSearchUrl(form);
    this.inFlightQuery = this.hasInputTarget ? this.inputTarget.value : null;

    // Retour visuel immédiat : le corps des résultats passe en squelette le temps de la
    // réponse. Sans ça, seul l'URL changeait.
    skeletonizeSearchResults(frame);

    if (document.activeElement === this.inputTarget) {
      this.restoreFocusAfterLoad = true;
    }

    const isGlobalSearch = form.dataset.searchGlobal === 'true';
    const updateHistory = form.dataset.searchUpdateHistory === 'true';
    const formPath = new URL(form.action, window.location.origin).pathname.replace(/\/$/, '');
    const currentPath = window.location.pathname.replace(/\/$/, '');
    const historyPath = form.dataset.searchHistoryPath;

    if (isGlobalSearch && updateHistory && historyPath) {
      const historyUrl = new URL(historyPath, window.location.origin);
      const searchUrl = new URL(url.toString(), window.location.origin);
      historyUrl.search = '';
      const queryParam = searchUrl.searchParams.get('query');
      if (queryParam && queryParam.trim().length > 0) {
        historyUrl.searchParams.set('query', queryParam);
      }
      if (currentPath === historyUrl.pathname.replace(/\/$/, '')) {
        window.history.replaceState(window.history.state, '', historyUrl);
      }
    } else if ((isGlobalSearch ? updateHistory : true) && currentPath === formPath) {
      window.history.replaceState(window.history.state, '', url);
    }

    // Never set frame.src here: Turbo's Range renderer duplicates <table> markup
    // out of nested index frames (ghost table over the show tabs).
    this.fetchSearchResultsIntoFrame(frame, url, { global: isGlobalSearch });
  }

  async fetchSearchResultsIntoFrame(frame, url, { global } = {}) {
    const isGlobal = global ?? this.isGlobalSearch;
    this.searchAbortController?.abort();
    this.searchAbortController = new AbortController();
    const signal = this.searchAbortController.signal;
    const query = this.hasInputTarget ? this.inputTarget.value.trim() : '';
    const responseFrameId = frame.id;

    try {
      frame.setAttribute('busy', '');
      if (isGlobal) {
        if (query.length > 2) {
          frame.innerHTML = `
            <div class="global-search__panel global-search__panel--loading" data-search-target="results">
              <div class="global-search__loading" role="status" aria-live="polite">
                <span class="spinner-border spinner-border-thin text-muted" aria-hidden="true"></span>
                <span class="visually-hidden">Chargement</span>
              </div>
            </div>`;
        } else if (!this.isPageSearch) {
          frame.innerHTML = '';
        }
      }

      const response = await fetch(url.toString(), {
        headers: {
          Accept: 'text/html',
          'Turbo-Frame': responseFrameId,
          'X-Requested-With': 'XMLHttpRequest',
        },
        credentials: 'same-origin',
        signal,
      });
      if (!response.ok) {
        if (isGlobal) {
          frame.innerHTML = `<div class="global-search__panel" data-search-target="results"><div class="global-search__empty text-muted">Aucun résultat</div></div>`;
        }
        return;
      }

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const newFrame = doc.getElementById(frame.id) || doc.getElementById(responseFrameId);
      if (!newFrame) {
        if (isGlobal) {
          frame.innerHTML = `<div class="global-search__panel" data-search-target="results"><div class="global-search__empty text-muted">Aucun résultat</div></div>`;
        }
        return;
      }

      frame.replaceChildren(...newFrame.children);
      frame.dispatchEvent(new Event('turbo:frame-load', { bubbles: true }));
    } catch (error) {
      if (error?.name === 'AbortError') {
        return;
      }
      console.warn('Search fetch failed', error);
      if (isGlobal) {
        frame.innerHTML = `<div class="global-search__panel" data-search-target="results"><div class="global-search__empty text-muted">Aucun résultat</div></div>`;
      }
    } finally {
      frame.removeAttribute('busy');
      // Seule la requête encore en cours lève l'état d'attente : une requête annulée ne doit
      // pas déclarer « prêt » un conteneur que la frappe suivante vient de remettre en attente.
      if (this.searchAbortController?.signal === signal) {
        clearSearchResultsSkeleton(frame);
      }
    }
  }

  buildSearchUrl(form) {
    const url = new URL(form.action, window.location.origin);
    const query = this.hasInputTarget ? this.inputTarget.value : '';
    const trimmedLength = query.trim().length;

    new FormData(form).forEach((value, key) => {
      if (key === 'query') return;
      if (value !== '') url.searchParams.set(key, value.toString());
    });

    if (trimmedLength > 2) {
      url.searchParams.set('query', query);
    } else {
      url.searchParams.delete('query');
    }
    url.searchParams.delete('page');

    return url;
  }

  handleFrameLoad(event) {
    if (!this.hasFormTarget) return;

    const frame = event.target;
    const frameId = frame?.id || '';
    const ownedFrameId = this.formTarget.dataset.turboFrame;
    if (!ownedFrameId || frameId !== ownedFrameId) return;

    const currentQuery = this.hasInputTarget ? this.inputTarget.value : null;
    if (this.inFlightQuery !== null && currentQuery !== this.inFlightQuery) {
      this.inFlightQuery = null;
      clearTimeout(this.timeout);
      this.loadSearchResults();
      return;
    }

    this.inFlightQuery = null;
    this.clearResultSelection();

    if (!this.restoreFocusAfterLoad || !this.hasInputTarget) return;

    this.restoreFocusAfterLoad = false;
    this.inputTarget.focus({ preventScroll: true });
    const length = this.inputTarget.value.length;
    this.inputTarget.setSelectionRange(length, length);
  }

  handleFocus() {
    this.setInlineSpotlight(true);
    const queryLength = this.inputTarget.value.trim().length;
    if (this.hasResultsTarget && queryLength > 1) {
      this.resultsTarget.classList.remove('d-none');
    }
  }

  hideResults() {
    this.keyboardNavActive = false;
    this.clearResultSelection();
    if (this.hasResultsTarget) {
      this.resultsTarget.classList.add('d-none');
    }
  }

  resultLinks() {
    if (!this.hasResultsTarget || this.resultsTarget.classList.contains('d-none')) return [];
    return Array.from(this.resultsTarget.querySelectorAll('[data-search-result-link]'));
  }

  clearResultSelection() {
    if (!this.hasResultsTarget) return;
    this.resultsTarget
      .querySelectorAll('.global-search-results__item.is-active, .global-search-results__item.is-keyboard-active')
      .forEach((item) => {
        item.classList.remove('is-active', 'is-keyboard-active');
      });
  }

  moveResultSelection(delta) {
    const links = this.resultLinks();
    if (!links.length) return;

    const items = links.map((link) => link.closest('.global-search-results__item')).filter(Boolean);
    if (!items.length) return;

    // Ignore le hover tant que la souris ne bouge pas vraiment (évite le vol
    // de surbrillance par l’item sous le curseur au passage au clavier).
    this.keyboardNavActive = true;

    let index = items.findIndex((item) => item.classList.contains('is-keyboard-active'));
    if (index === -1) {
      index = delta > 0 ? 0 : items.length - 1;
    } else {
      items[index].classList.remove('is-active', 'is-keyboard-active');
      index = (index + delta + items.length) % items.length;
    }

    items[index].classList.add('is-active', 'is-keyboard-active');
    items[index].scrollIntoView({ block: 'nearest' });
  }

  openActiveResult() {
    const activeLink =
      this.resultsTarget?.querySelector(
        '.global-search-results__item.is-keyboard-active [data-search-result-link], .global-search-results__item.is-active [data-search-result-link]'
      ) || null;
    if (!activeLink) return false;

    this.releaseForNavigation();
    activeLink.click();
    return true;
  }

  // Un résultat choisi lance une visite. En ligne, on lâche tout de suite le spotlight, sinon le
  // voile peut rester collé. La palette, elle, se referme d'un bloc au départ de la visite (cf.
  // global_search_palette_controller) : masquer sa liste ici la ferait disparaître seule, avant
  // le champ et le voile.
  releaseForNavigation() {
    if (this.isPaletteSearch) return;

    this.hideResults();
    this.setInlineSpotlight(false);
  }

  handleResultsMouseMove() {
    if (!this.keyboardNavActive) return;
    this.keyboardNavActive = false;
  }

  handleResultsMouseOver(event) {
    if (!this.isGlobalSearch || !this.hasResultsTarget) return;
    // Pendant la nav clavier, ignorer les mouseover synthétiques (curseur immobile).
    if (this.keyboardNavActive) return;

    const item = event.target.closest?.('.global-search-results__item');
    if (!item || !this.resultsTarget.contains(item)) return;

    if (!item.querySelector('[data-search-result-link]')) return;
    if (item.classList.contains('is-active')) return;

    this.clearResultSelection();
    item.classList.add('is-active');
  }

  handleBlur(event) {
    if (this.isPageSearch) return;

    const root = this.globalSearchRoot;
    if (this.hasResultsTarget && !this.resultsTarget.contains(event.relatedTarget)) {
      this.hideResults();
    }
    // Délai court : un clic dans les résultats ne doit pas tuer le spotlight trop tôt
    window.setTimeout(() => {
      if (root?.contains(document.activeElement)) return;
      this.setInlineSpotlight(false);
    }, 100);
  }

  handleClickOutside(event) {
    if (this.isPageSearch) return;

    const root = this.globalSearchRoot;
    // Clic sur un hit / « Voir plus » : navigation turbo_frame=_top.
    if (event.target.closest?.('.global-search-results__link, .global-search-results__section-see-all')) {
      this.releaseForNavigation();
      return;
    }

    // Index / palette : comportement habituel. Globale inline : .global-search seulement
    // (sur show, this.element englobe toute la page).
    if (this.isGlobalSearch && !this.isPaletteSearch) {
      if (root?.contains(event.target)) return;
    } else if (this.element.contains(event.target)) {
      return;
    }

    this.hideResults();
    this.setInlineSpotlight(false);
  }

  handleKeyDown(event) {
    // Palette en train de se refermer sur un résultat choisi : le champ garde le focus le temps
    // du fondu, mais plus rien n'est à piloter — une 2e Entrée relancerait la visite.
    if (this.isPaletteSearch && !this.element.classList.contains('is-visible')) {
      event.preventDefault();
      return;
    }

    if (event.key === 'Escape' && this.isGlobalSearch && !this.isPaletteSearch && !this.isPageSearch) {
      event.preventDefault();
      this.hideResults();
      this.setInlineSpotlight(false);
      this.inputTarget.blur();
      return;
    }

    if (this.isGlobalSearch && this.resultLinks().length) {
      if (event.key === 'ArrowDown') {
        event.preventDefault();
        this.moveResultSelection(1);
        return;
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault();
        this.moveResultSelection(-1);
        return;
      }
      if (event.key === 'Enter') {
        event.preventDefault();
        if (this.openActiveResult()) return;
        // Pas de sélection clavier : ouvrir le premier résultat.
        const first = this.resultLinks()[0];
        if (first) {
          this.releaseForNavigation();
          first.click();
        }
        return;
      }
    }

    if (event.key !== 'Enter') return;

    // Index Rails Admin : la recherche est déjà déclenchée au fil de la saisie.
    event.preventDefault();
  }

  preventSubmit(event) {
    event.preventDefault();
  }
}
